import * as React from 'react'
import { loadConnectAndInitialize, type StripeConnectInstance } from '@stripe/connect-js'
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectBalanceReport,
  ConnectComponentsProvider,
  ConnectDocuments,
  ConnectNotificationBanner,
  ConnectPayments,
  ConnectPayoutReconciliationReport,
  ConnectPayouts,
} from '@stripe/react-connect-js'
import type { SalesBusinessDto } from '@lib/dto/sales-business.dto'
import { useQueryClient } from '@tanstack/react-query'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Tabs } from '@/components/ui/tabs'
import { requestErrorMessage } from '@/helpers/apiClient'
import { salesKeys } from '@/hooks/queryKeys'
import { salesService } from '@/services/SalesService'

type Tab = 'payments' | 'payouts' | 'reports' | 'documents' | 'details'

const TABS: { value: Tab; label: string }[] = [
  { value: 'payments', label: 'Payments' },
  { value: 'payouts', label: 'Payouts' },
  { value: 'reports', label: 'Reports' },
  { value: 'documents', label: 'Tax forms' },
  { value: 'details', label: 'Business details' },
]

function isTab(value: string): value is Tab {
  return TABS.some((t) => t.value === value)
}

/**
 * Stripe's pages for a business, inside the app (docs/sales.spec.md, "Stripe"):
 * verifying it, then its payments, payouts, reports, tax forms and details. The
 * business has no Stripe login of its own; this is where it sees its money.
 * Refunds aren't offered in Stripe's list: they go through the event's Sales
 * section, which checks the Refunds permission.
 */
export function BusinessPortal({ business }: { business: SalesBusinessDto }) {
  const queryClient = useQueryClient()
  const [instance, setInstance] = React.useState<StripeConnectInstance | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [tab, setTab] = React.useState<Tab>('payments')

  React.useEffect(() => {
    let cancelled = false
    salesService
      .businessSession(business.id)
      .then((first) => {
        if (cancelled) return
        let pending: string | null = first.clientSecret
        setInstance(
          loadConnectAndInitialize({
            publishableKey: first.publishableKey,
            // The first secret was just fetched; after that, each one is fresh.
            fetchClientSecret: async () => {
              if (pending !== null) {
                const secret = pending
                pending = null
                return secret
              }
              return (await salesService.businessSession(business.id)).clientSecret
            },
          }),
        )
      })
      .catch((e: Error) => {
        if (!cancelled) setError(requestErrorMessage(e))
      })
    return () => {
      cancelled = true
    }
  }, [business.id])

  if (error !== null) return <ErrorState message={error} />
  if (instance === null) return <DetailSkeleton />

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: salesKeys.all })

  return (
    <ConnectComponentsProvider connectInstance={instance}>
      <div className="space-y-4">
        <ConnectNotificationBanner />
        {business.status === 'active' ? (
          <>
            <Tabs items={TABS} value={tab} onValueChange={(v) => isTab(v) && setTab(v)} />
            {tab === 'payments' ? <ConnectPayments /> : null}
            {tab === 'payouts' ? <ConnectPayouts /> : null}
            {tab === 'reports' ? (
              <div className="space-y-8">
                <ConnectBalanceReport />
                <ConnectPayoutReconciliationReport />
              </div>
            ) : null}
            {tab === 'documents' ? <ConnectDocuments /> : null}
            {tab === 'details' ? <ConnectAccountManagement /> : null}
          </>
        ) : (
          <ConnectAccountOnboarding onExit={refresh} />
        )}
      </div>
    </ConnectComponentsProvider>
  )
}
